import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Check, CheckCheck, Reply, Send, Video } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyContent, setReplyContent] = useState("");

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Nachrichten");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`);
      fetchMessages();
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedMessage.sender_id,
        subject: `Re: ${selectedMessage.subject}`,
        content: replyContent,
        parent_id: selectedMessage.id
      });
      toast.success("Antwort gesendet");
      setReplyDialogOpen(false);
      setReplyContent("");
      fetchMessages();
    } catch (error) {
      toast.error("Fehler beim Senden der Antwort");
    }
  };

  const handleOpenMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      handleMarkAsRead(message.id);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), "dd. MMM yyyy, HH:mm", { locale: de });
  };

  const unreadCount = messages.filter(m => !m.is_read && m.receiver_id === user?.id).length;

  return (
    <ParticipantLayout title="Nachrichten">
      <div className="space-y-6" data-testid="participant-messages">
        {/* Summary */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{messages.length} Nachrichten</p>
                {unreadCount > 0 && (
                  <p className="text-sm text-primary">{unreadCount} ungelesen</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Messages List */}
          <Card className="bg-card border-border lg:col-span-1">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg">Posteingang</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Nachrichten</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-border">
                    {messages.map((msg) => (
                      <button
                        key={msg.id}
                        onClick={() => handleOpenMessage(msg)}
                        className={`w-full p-4 text-left hover:bg-secondary/30 transition-colors ${
                          selectedMessage?.id === msg.id ? "bg-secondary/50" : ""
                        } ${!msg.is_read && msg.receiver_id === user?.id ? "border-l-2 border-l-primary" : ""}`}
                        data-testid={`message-item-${msg.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm truncate">
                            {msg.sender_name}
                          </span>
                          {!msg.is_read && msg.receiver_id === user?.id && (
                            <Badge className="status-active rounded-sm text-xs">Neu</Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium text-primary truncate">{msg.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(msg.created_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Message Detail */}
          <Card className="bg-card border-border lg:col-span-2">
            <CardContent className="p-6">
              {selectedMessage ? (
                <div className="space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-primary mb-2">
                        {selectedMessage.subject}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Von: {selectedMessage.sender_name}</span>
                        <span>•</span>
                        <span>{formatDate(selectedMessage.created_at)}</span>
                        {selectedMessage.parent_id && (
                          <>
                            <span>•</span>
                            <Badge variant="outline" className="text-xs">
                              <Reply className="h-3 w-3 mr-1" />
                              Antwort
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {selectedMessage.is_read ? (
                        <CheckCheck className="h-4 w-4 text-green-500" />
                      ) : (
                        <Check className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-secondary/30 rounded-sm min-h-[200px]">
                    <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
                  </div>

                  {/* Microsoft Teams Link Button */}
                  {selectedMessage.teams_link && (
                    <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                            <Video className="h-5 w-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="font-medium text-blue-500">Live-Unterricht verfügbar</p>
                            <p className="text-sm text-muted-foreground">Microsoft Teams Meeting</p>
                          </div>
                        </div>
                        <Button
                          className="gap-2 bg-blue-600 hover:bg-blue-700"
                          onClick={() => window.open(selectedMessage.teams_link, '_blank')}
                          data-testid="join-teams-btn"
                        >
                          <Video className="h-4 w-4" />
                          Teams beitreten
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={() => setReplyDialogOpen(true)}
                      className="gap-2"
                      data-testid="reply-button"
                    >
                      <Reply className="h-4 w-4" />
                      Antworten
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
                  <p>Wählen Sie eine Nachricht aus</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Reply Dialog */}
        <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
          <DialogContent className="bg-card border-border" data-testid="reply-dialog">
            <DialogHeader>
              <DialogTitle>Antworten</DialogTitle>
              <DialogDescription>
                Antwort an: {selectedMessage?.sender_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleReply} className="space-y-4">
              <div className="p-3 bg-secondary/30 rounded-sm text-sm">
                <p className="text-muted-foreground mb-1">Betreff:</p>
                <p className="font-medium">Re: {selectedMessage?.subject}</p>
              </div>
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Ihre Antwort..."
                rows={5}
                required
                className="bg-background border-input resize-none"
                data-testid="reply-content-input"
              />
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setReplyDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="gap-2" data-testid="send-reply-btn">
                  <Send className="h-4 w-4" />
                  Senden
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </ParticipantLayout>
  );
};

export default Messages;
